// Plan v3 (R-73, build/specs/R-73-plan-v3.md): Saved is every activity the person saved, grouped by town (groupSaved). It is not a
// new store: Save is the same `plan { add: { item } }` call Add was (the person's draft in that item's city), so the taste
// signal (plan_items.added_by + the swipes yes) is unchanged, and on sign-in it moves with the person like any plan.
// Saved = the items of every undated plan they own (their drafts, plus older undated plans), once each.
import { noteSaveKinds } from './kinds'
import { useSyncExternalStore } from 'react'
import { callFn, ensureSession, supabase } from './api'
import { addItem, addedNow, consentSeen, forgetAdded, noteItem, removeItem, useBrowsePlan } from './browse'
import type { Plan, PlanItemData } from './plan'
import { kmBetween } from './fit'
import { savedCountOf } from './groupPlan'
import { onOwnerChange } from './owner'
import { latestLoad } from './latestLoad'

export type SavedEntry = {
  itemId: string; planItemId: string; planId: string
  /** The city slug and label of the plan it is saved in (the item's city: Save goes to that city's draft). */
  city: string; cityLabel: string; tz: string | null
  item: PlanItemData; gone?: 'cancelled' | 'past'
}
export type SavedCity = { city: string; label: string; tz: string | null; entries: SavedEntry[] }

// B-148 (lane D, 10/15): the masthead count and the Saved page read the SAME list. `pending` = saves this tab sent (Save, or
// Add to a group, which also saves since pin 571) that the next load will show; the count adds only those, never the
// Browse store's "added" flags (those also mark things put on a dated itinerary or a group, and could be another
// account's, from a cached page). Saved itself shows the person's undated plans (their drafts): a dated itinerary
// lives under Itineraries and a group's own list on its group page.
type State = { entries: SavedEntry[] | null; loading: boolean; error: boolean; removed: Set<string>; pending: Map<string, number>; complete?: boolean }
let state: State = { entries: null, loading: false, error: false, removed: new Set(), pending: new Map() }
const subs = new Set<() => void>()
const set = (p: Partial<State>) => { state = { ...state, ...p }; subs.forEach((f) => f()) }
const subscribe = (f: () => void) => { subs.add(f); return () => { subs.delete(f) } }

type ListRow = { id: string; party?: { group_id?: string } | null; mine?: boolean; is_draft?: boolean; start_date: string | null; end_date?: string | null; dest?: { city?: string; label?: string; tz?: string } | null; city?: string; item_count?: number; status?: string }
type GotUnscheduled = { id: string; item: PlanItemData | null; gone?: 'cancelled' | 'past' }
type Got = { plan: Plan & { start_date: string | null; dest: Plan['dest'] }; unscheduled?: GotUnscheduled[] }

let lastPlans: ListRow[] | null = null
// B-194 (10/16): when each unsave this tab sent finished (Infinity while it is on its way). A Saved load that started
// before an unsave finished read the list with that thing still on it; its answer must not bring it back (the listing
// sheet showed Saved for a thing that was gone, and a second tap saved it again).
const removedAt = new Map<string, number>()
/** Did the last Saved load read every undated plan? (a plan whose get failed is missing from it: B-194) */
let lastComplete = false
/** Whose list the last full load read (the tester's log compares it with the account that saved; B-194 part 3). */
let lastUser: string | null = null
/** The plan list the last Saved load read (lib/checkin.ts reads the dated ones from it: no second list call). */
export const savedPlansNow = () => lastPlans
type LoadOpts = { onlyWithSession?: boolean }
// release 1016: a call while a load runs gets one more load after it (lib/latestLoad.ts), never the older answer, so a
// Save that lands mid-load still shows; a change of person voids the running load's answer (B-161)
// B-199: a read that never answers (seen once in WebKit: a queued tap then sent nothing) held every tap queued behind it
// forever; each step of a list read now gives up after 8 s, so the load ends incomplete and the next one asks again
const LOAD_CALL_MS = 8_000
const inTime = <T,>(p: Promise<T>): Promise<T> => new Promise<T>((ok, no) => {
  const t = setTimeout(() => { console.warn('[saved] list read timed out'); no(new Error('saved: list read timed out')) }, LOAD_CALL_MS)
  p.then((v) => { clearTimeout(t); ok(v) }, (e) => { clearTimeout(t); no(e) })
})
const loader = latestLoad<LoadOpts>(async (opts, live) => {
  set({ loading: true, error: false })
  const startedAt = Date.now()
  try {
    // the masthead count never starts a session for a visitor who has not done anything yet
    // B-194: no session yet is not "nothing saved": the list stays unknown (cards keep their own flag) instead of an empty
    // answer that made a saved thing's button read Save (WebKit gate: a tap then sent an add, 409)
    if (opts.onlyWithSession && !(await supabase.auth.getSession()).data.session) { if (live()) { lastComplete = false; set({ entries: state.entries, loading: loader.more(), complete: false }) } return }
    lastUser = (await inTime(ensureSession())).user.id
    const { plans } = await inTime(callFn<{ plans: ListRow[] }>('plan', { list: true }))
    if (!live()) return
    lastPlans = plans
    // R-29: a group's shared plan is not the person's own saves (it lives on the group page)
    const undated = plans.filter((p) => p.mine !== false && !p.start_date && (p.item_count ?? 1) > 0 && !p.party?.group_id)
    const got = await Promise.all(undated.map((p) => inTime(callFn<Got>('plan', { get: p.id })).catch(() => null)))
    if (!live()) return
    lastComplete = got.every(Boolean)
    const seen = new Set<string>()
    const out: SavedEntry[] = []
    // drafts first (the person's live basket), then older undated plans
    const order = undated.map((p, i) => ({ p, g: got[i] })).sort((a, b) => Number(Boolean(b.p.is_draft)) - Number(Boolean(a.p.is_draft)))
    for (const { p, g } of order) {
      if (!g) continue
      const dest = g.plan.dest ?? p.dest ?? {}
      const city = String(dest.city ?? p.city ?? 'los-angeles')
      const label = String(dest.label && city !== 'los-angeles' ? dest.label : city === 'los-angeles' ? 'Los Angeles' : dest.label ?? city)
      // newest saves first: the server keeps add order
      for (const u of [...(g.unscheduled ?? [])].reverse()) {
        if (!u.item || seen.has(u.item.id)) continue
        seen.add(u.item.id)
        out.push({ itemId: u.item.id, planItemId: u.id, planId: p.id, city, cityLabel: label, tz: dest.tz ?? null, item: u.item, ...(u.gone ? { gone: u.gone } : {}) })
      }
    }
    // a pending save the list now shows is done; one sent well before this load that still is not on it never landed
    // and is dropped (the count must never stay above the page)
    const pending = new Map([...state.pending].filter(([id, at]) => !seen.has(id) && at >= startedAt - 5_000))
    // B-194: an unsave this load may not have seen stays hidden; one that finished before it started is in its answer
    for (const [id, at] of removedAt) if (at <= startedAt) removedAt.delete(id)
    set({ entries: out, loading: loader.more(), removed: new Set(removedAt.keys()), pending, complete: lastComplete })
  } catch {
    if (live()) { lastComplete = false; set({ loading: loader.more(), error: true, complete: false }) }
  }
}, (a, b) => ({ onlyWithSession: Boolean(a.onlyWithSession && b.onlyWithSession) }))
/** Load (or reload) Saved from the server. Concurrent reads share one load; `after` (a save just landed) waits for a load
 *  that started after the call, never the older answer. */
export function loadSaved(opts: LoadOpts = {}, after = false): Promise<void> {
  return loader.run(opts, after)
}

export const useSavedStore = () => useSyncExternalStore(subscribe, () => state)
/** The loaded Saved entries outside React (lib/checkin.ts); null before the first load. */
export const savedEntriesNow = (): SavedEntry[] | null => state.entries

/** Saved, grouped by city: the city with the most saved things first, Los Angeles first on a tie. */
export function groupByCity(entries: SavedEntry[]): SavedCity[] {
  const m = new Map<string, SavedCity>()
  for (const e of entries) {
    const g = m.get(e.city) ?? { city: e.city, label: e.cityLabel, tz: e.tz, entries: [] }
    g.entries.push(e)
    m.set(e.city, g)
  }
  return [...m.values()].sort((a, b) => b.entries.length - a.entries.length || Number(b.city === 'los-angeles') - Number(a.city === 'los-angeles'))
}

/** "Chino Hills 91709" -> "Chino Hills" (a label that is only a zip stays as it is). */
export const placeName = (label: string): string => label.replace(/\s+\d{5}(?:-\d{4})?$/, '').trim() || label

/**
 * The town or area an item is in (Alvin 10/09: "the site says LA when it was chino hills stuff i saved"): its own
 * neighborhood/locality, else the town in its address ("15975 El Prado Road, Chino", "301 Garey Ave, Pomona, CA 91766";
 * for an event that is the venue's). Null when neither says.
 */
export function townOf(item: Pick<PlanItemData, 'neighborhood' | 'address'>): string | null {
  const n = item.neighborhood?.trim()
  if (n) return n
  const parts = (item.address ?? '').split(',').map((x) => x.trim()).filter(Boolean)
  const tail = /^(?:usa|us|united states|[A-Z]{2}(?:\s+\d{5}(?:-\d{4})?)?|\d{5}(?:-\d{4})?)$/i
  while (parts.length > 1 && tail.test(parts[parts.length - 1])) parts.pop()
  const t = parts.length > 1 ? parts[parts.length - 1] : null
  return t && !/\d/.test(t) ? t : null
}

/** A Saved section. near: within the home distance of their home; town: one town; other: town unknown; city: another city. */
export type SavedGroup = { key: string; kind: 'near' | 'town' | 'other' | 'city'; label: string; entries: SavedEntry[] }
export type SavedHome = { slug: string; label: string | null; lat: number | null; lng: number | null }
/** More town sections than this, at least half of them one save each, reads as a scatter. */
const MAX_TOWNS = 4

function byTown(xs: SavedEntry[], fallback: (e: SavedEntry) => string | null = () => null): SavedGroup[] {
  const m = new Map<string, SavedGroup>()
  const other: SavedEntry[] = []
  for (const e of xs) {
    const t = townOf(e.item) ?? fallback(e)
    if (!t) { other.push(e); continue }
    const k = t.toLowerCase()
    const g = m.get(k) ?? { key: `town:${k}`, kind: 'town' as const, label: t, entries: [] }
    g.entries.push(e)
    m.set(k, g)
  }
  const towns = [...m.values()].sort((a, b) => b.entries.length - a.entries.length)
  return other.length ? [...towns, { key: 'other', kind: 'other', label: '', entries: other }] : towns
}

/** Which layout Saved uses: by town, unless that scatters into many one-save towns and we know their home. */
export function savedLayout(entries: SavedEntry[], home: SavedHome): 'town' | 'near' {
  const named = byTown(entries.filter((e) => e.city === home.slug)).filter((g) => g.kind === 'town')
  const scattered = named.length > MAX_TOWNS && named.filter((g) => g.entries.length === 1).length * 2 >= named.length
  return scattered && home.label && home.lat != null && home.lng != null ? 'near' : 'town'
}

/**
 * Saved, grouped where each thing is (Alvin 10/09). In their home city: by town ("Chino", "Claremont"), or, when that
 * scatters, "Near {home}" (within `homeMiles`; each row names its town) and then the farther towns. Other cities (Lisbon
 * vs LA) stay one section each, after.
 */
export function groupSaved(entries: SavedEntry[], home: SavedHome, homeMiles: number, layout = savedLayout(entries, home)): SavedGroup[] {
  const local = entries.filter((e) => e.city === home.slug)
  const away = groupByCity(entries.filter((e) => e.city !== home.slug)).map((c): SavedGroup => ({ key: `city:${c.city}`, kind: 'city', label: c.label, entries: c.entries }))
  // blind round 2 (Rosa): a thing with no town on record but within the home distance of home goes under home's own name
  // ("Austin"), not "Other places" (Seaholm, 0.3 mi from downtown)
  const homeName = home.label && !/\d/.test(placeName(home.label)) ? placeName(home.label) : null
  const nearHome = (e: SavedEntry) => {
    if (!homeName || home.lat == null || home.lng == null || e.item.lat == null || e.item.lng == null) return null
    const km = kmBetween({ lat: home.lat, lng: home.lng }, { lat: e.item.lat, lng: e.item.lng })
    return km != null && km <= homeMiles * 1.609344 ? homeName : null
  }
  if (layout === 'town' || !home.label || home.lat == null || home.lng == null) return [...byTown(local, nearHome), ...away]
  const centre = { lat: home.lat, lng: home.lng }
  const near: SavedEntry[] = []
  const far: SavedEntry[] = []
  for (const e of local) {
    const km = e.item.lat != null && e.item.lng != null ? kmBetween(centre, { lat: e.item.lat, lng: e.item.lng }) : null
    // no location: counts as near (it is saved in their home city), the same rule as the itinerary's area
    if (km === null || km <= homeMiles * 1.609344) near.push(e)
    else far.push(e)
  }
  const first: SavedGroup[] = near.length ? [{ key: 'near', kind: 'near', label: placeName(home.label), entries: near }] : []
  return [...first, ...byTown(far), ...away]
}

/**
 * Is this item saved? This session's saves (the browse store) win; then the loaded Saved list; before it loads, the
 * server's own flag on the card.
 */
export function useIsSaved(itemId: string, serverFlag = false): 'saving' | 'saved' | 'failed' | null {
  const s = useSavedStore()
  const { added } = useBrowsePlan()
  return savedStateOf(s, added[itemId]?.status, itemId, serverFlag)
}
function savedStateOf(s: State, st: string | undefined, itemId: string, serverFlag: boolean): 'saving' | 'saved' | 'failed' | null {
  if (st === 'adding') return 'saving'
  if (st === 'failed') return 'failed'
  if (s.removed.has(itemId)) return null
  if (s.pending.has(itemId)) return 'saved'
  // B-148: once Saved has loaded it is the answer (a thing on a dated itinerary or only in a group is not "saved")
  if (s.entries) return s.entries.some((e) => e.itemId === itemId) ? 'saved' : null
  if (st === 'added') return 'saved'
  return serverFlag ? 'saved' : null
}

/** How many things are saved (the masthead count): exactly what the Saved page lists, plus saves still on their way to it. */
export function useSavedCount(): number | null {
  const s = useSavedStore()
  return savedCountOf(s.entries, s.removed, s.pending)
}
/** A save the server already made for this person (pin 571: Add to a group also saves): count it now, list it on reload. */
export function savedElsewhere(itemId: string): void {
  const removed = new Set(state.removed)
  removed.delete(itemId)
  removedAt.delete(itemId)
  set({ removed, pending: new Map(state.pending).set(itemId, Date.now()) })
  void loadSaved({}, true) // after this save: never the load already running
}

/** Save: exactly the old Add (the draft in that item's city). Saved reloads after, so it shows under its city. */
export async function saveItem(item: PlanItemData): Promise<void> {
  noteItem(item)
  const removed = new Set(state.removed)
  removed.delete(item.id)
  removedAt.delete(item.id)
  set({ removed, pending: new Map(state.pending).set(item.id, Date.now()) })
  try {
    await addItem(item, undefined, { force: true }) // an itinerary's or group's "added" flag is not a save
  } catch (e) {
    const pending = new Map(state.pending)
    pending.delete(item.id)
    set({ pending })
    throw e
  }
  noteSaveKinds(item) // level-2 kinds: the 2nd save of one kind asks "More <kind>?" (lib/kinds.ts)
  consentSeen() // the consent line showed before this first save (ConsentLine); it does not come back
  // addItem logs browse_add (a registered app_events name); no second event per save
  void loadSaved({}, true) // after this save: never the load already running
}

/** Unsave: takes it out of whichever plan holds it as a save. */
export async function unsaveItem(itemId: string): Promise<void> {
  const removed = new Set(state.removed)
  removed.add(itemId)
  const pending = new Map(state.pending)
  pending.delete(itemId)
  removedAt.set(itemId, Infinity)
  set({ removed, pending })
  try {
    // B-194: a tap before Saved has loaded (the listing sheet opened by link) waits for the list, so the remove knows
    // which plan row to delete instead of guessing from this tab's own saves. The load it joins may be the masthead's
    // (empty before the session is back) or may have lost a plan; then one fresh load decides.
    const find = () => state.entries?.find((e) => e.itemId === itemId)
    if (!find()) await loadSaved({}).catch(() => {})
    if (!find()) await loadSaved({}, true).catch(() => {})
    const entry = find()
    console.info('[saved] remove ' + JSON.stringify({ item: itemId, row: Boolean(entry) }))
    if (entry) {
      await callFn('plan', { remove: entry.planItemId })
      forgetAdded(itemId)
      set({ entries: (state.entries ?? []).filter((e) => e.itemId !== itemId) })
    } else {
      // never show a thing as removed without a remove the server answered (B-194, WebKit gate: no call, a false Save);
      // the list is read again so the button shows the truth
      if (!(await removeItem(itemId))) { void loadSaved({}, true).catch(() => {}); throw new Error('saved: could not find it to remove') }
      forgetAdded(itemId)
    }
    if (removedAt.get(itemId) === Infinity) removedAt.set(itemId, Date.now())
  } catch (e) {
    removedAt.delete(itemId)
    const back = new Set(state.removed)
    back.delete(itemId)
    set({ removed: back })
    throw e
  }
}

/**
 * B-194: what a Save / Saved tap acts on. When the Saved list is not settled (not loaded, loading, or the last load missed a
 * plan) it waits for a full list first, so a tap that lands while the sheet is still finding out never sends an add for a
 * saved thing or a remove with no row. Resolves to whether the thing is saved now.
 */
/** B-194: has a full Saved list answered yet? (the listing sheet shows its button busy until it has) */
export const useSavedKnown = (): boolean => { const s = useSavedStore(); return s.entries !== null && Boolean(s.complete) }
/** Why a tap sent nothing, for the tester's console log (B-194 part 3). */
export function savedTrace(itemId: string): Record<string, unknown> {
  const e = state.entries?.find((x) => x.itemId === itemId)
  return { user: lastUser?.slice(0, 8) ?? null, entries: state.entries?.length ?? null, has: Boolean(e), complete: lastComplete, loading: state.loading, pending: state.pending.has(itemId), removed: state.removed.has(itemId), added: addedNow(itemId)?.status ?? null }
}

/**
 * B-200: is it saved, by what this tab knows right now (no waiting)? null = cannot tell: the last list read missed a plan
 * (so "not on the list" proves nothing) and nothing else in this tab says it is saved. A tap then sends neither add nor
 * remove (an add for a saved thing was the B-194 shape).
 */
export function savedVerdict(itemId: string, serverFlag = false): boolean | null {
  const added = addedNow(itemId)?.status
  if (savedStateOf(state, added, itemId, serverFlag) === 'saved') return true
  if (lastComplete || state.removed.has(itemId)) return false
  return added === 'added' || serverFlag ? true : null
}

export async function settleSaved(itemId: string, serverFlag = false): Promise<boolean | null> {
  if (!state.entries || state.loading || !lastComplete) {
    await loadSaved({}).catch(() => {})
    if (!lastComplete) await loadSaved({}, true).catch(() => {})
  }
  return savedVerdict(itemId, serverFlag)
}

// a change of person in this tab: forget the last person's Saved list in memory (lib/owner.ts; B-148, B-161)
onOwnerChange(() => { loader.reset(); lastPlans = null; removedAt.clear(); set({ entries: null, loading: false, error: false, removed: new Set(), pending: new Map() }) })
